import { Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Case } from '../../core/services/case.model';

/**
 * Shared "Open a case" picker flyout — the searchable browse overlay used by
 * every rate surface's rail (broker CaseBox, /rates, /products). Renders an
 * "Open…" button + an anchored overlay listing cases with client context
 * (state · age · term · premium) and a name/state search. Presentational:
 * takes the case list + active id, emits the picked id; the host owns loading
 * the list and reacting to the pick.
 */
@Component({
  selector: 'app-case-picker-flyout',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <button type="button" class="open-btn" (click)="open()" title="Browse / open a case">Open…</button>
    @if (isOpen()) {
      <div class="cp-backdrop" (click)="isOpen.set(false)"></div>
      <div class="cp" (click)="$event.stopPropagation()">
        <div class="cp-h"><span>Open a case</span><span class="cp-x" (click)="isOpen.set(false)">✕</span></div>
        <div class="cp-search">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input [ngModel]="search()" (ngModelChange)="search.set($event)" placeholder="Search client or case…">
        </div>
        <div class="cp-list">
          @for (c of filtered(); track c.id) {
            <div class="cp-row" [class.sel]="activeId() === c.id" (click)="choose(c.id)">
              <div class="cp-txt">
                <span class="cp-nm">{{ c.name }}</span>
                <span class="cp-meta">{{ c.state || '—' }} · {{ c.age ?? '—' }}{{ c.termMin != null ? ' · ' + c.termMin + (c.termMax && c.termMax !== c.termMin ? '–' + c.termMax : '') + 'yr' : '' }}</span>
              </div>
              <span class="cp-prem">{{ c.premium != null ? '$' + (c.premium / 1000 | number:'1.0-0') + 'k' : '' }}</span>
            </div>
          }
          @if (filtered().length === 0) { <div class="cp-empty">No cases match.</div> }
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: inline-flex; }
    .open-btn { border: 1px solid var(--gold, #b8902a); background: #fff; color: #9a7a1f; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 4px 8px; cursor: pointer; white-space: nowrap; }
    .open-btn:hover { background: #fffdf7; }
    .cp-backdrop { position: fixed; inset: 0; z-index: 40; }
    .cp { position: fixed; left: 244px; top: 116px; width: 330px; z-index: 41; background: #fff; border: 1px solid var(--border); border-radius: 10px; box-shadow: 0 12px 32px rgba(13,42,69,.22); overflow: hidden; }
    .cp-h { display: flex; align-items: center; padding: 10px 12px; border-bottom: 1px solid var(--border-light); font-weight: 700; color: var(--navy); font-size: 13px; }
    .cp-h .cp-x { margin-left: auto; color: var(--muted); cursor: pointer; font-size: 15px; }
    .cp-search { display: flex; align-items: center; gap: 6px; margin: 8px 12px; border: 1px solid var(--border); border-radius: 6px; padding: 5px 8px; color: var(--muted); }
    .cp-search input { border: none; outline: none; flex: 1; font-family: var(--sans); font-size: 12px; color: var(--text); }
    .cp-list { max-height: 320px; overflow-y: auto; padding-bottom: 6px; }
    .cp-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; cursor: pointer; border-left: 3px solid transparent; }
    .cp-row:hover { background: var(--blue-10); }
    .cp-row.sel { background: #fffdf7; border-left-color: var(--gold, #b8902a); }
    .cp-txt { min-width: 0; display: flex; flex-direction: column; }
    .cp-nm { font-weight: 600; color: var(--text); font-size: 13px; }
    .cp-meta { font-size: 11px; color: var(--muted); font-family: var(--mono); }
    .cp-prem { margin-left: auto; font-family: var(--mono); font-weight: 700; color: var(--navy); font-size: 12px; }
    .cp-empty { padding: 14px 12px; color: var(--muted); font-size: 12px; }
  `],
})
export class CasePickerFlyoutComponent {
  readonly cases = input<Case[]>([]);
  readonly activeId = input<number | null>(null);
  readonly pick = output<number>();

  protected readonly isOpen = signal(false);
  protected readonly search = signal('');
  protected readonly filtered = computed<Case[]>(() => {
    const q = this.search().toLowerCase().trim();
    const list = this.cases();
    if (!q) return list;
    return list.filter(c => (c.name ?? '').toLowerCase().includes(q) || (c.state ?? '').toLowerCase().includes(q));
  });

  open(): void { this.search.set(''); this.isOpen.set(true); }
  choose(id: number): void { this.pick.emit(id); this.isOpen.set(false); }
}
